<template>
  <div
    class="post-card"
    :target="blok.target"
    :class="{ 'post-card--border': blok.border_color?.value }"
    :style="borderStyle"
  >
    <div
      class="post-card__image"
      :class="{ 'post-card__image--rounded-bottom': blok.image_rounded_bottom }"
      :style="imageStyle"
    >
      <div
        v-if="blok.top_content?.length"
        class="post-card__top-content"
        :style="topContentStyle"
      >
        <StoryblokComponent
          v-for="childBlok in blok.top_content"
          :key="childBlok._uid"
          :blok="childBlok"
        />
      </div>
    </div>
    <div class="post-card__content" :style="contentStyle">
      <StoryblokComponent
        v-for="childBlok in blok.blocks"
        :key="childBlok._uid"
        :blok="childBlok"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SbPostCardStoryblok } from '@/types'

interface Props {
  blok: SbPostCardStoryblok
}
const props = defineProps<Props>()

const imageStyle = computed(() => {
  const backgroundImage = `url(${props.blok.image?.filename || '/'})`

  // Without top_content, padding-top creates the aspect ratio (backward compat)
  if (!props.blok.top_content?.length) {
    return {
      backgroundImage,
      paddingTop: `${props.blok.image_ratio || 40}%`,
    }
  }

  return { backgroundImage }
})

const topContentStyle = computed(() => {
  const padding = props.blok.custom_padding != null
    ? `${props.blok.custom_padding}px`
    : '24px'
  return { padding }
})

const contentStyle = computed(() => {
  if (props.blok.custom_padding != null) {
    return { padding: `${props.blok.custom_padding}px` }
  }

  // Backward compat: legacy string padding field
  const padding = props.blok.padding === 'small' ? '32px' : '40px 48px'
  return { padding }
})

const borderStyle = computed(() => {
  if (!props.blok.border_color?.value) return {}
  return { '--post-card-border-color': props.blok.border_color.value }
})
</script>

<style lang="scss" scoped>
.post-card {
  display: block;
  width: 100%;

  &--border {
    border: 1px solid var(--post-card-border-color, #e5e7eb);
    border-radius: 12px;
    overflow: hidden;
  }

  &__image {
    background-position: center center;
    background-size: cover;
    background-repeat: no-repeat;

    &--rounded-bottom {
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }
  }

  &__top-content {
    padding: 24px;
  }

  &__content {
    padding: 40px 48px;
  }
}
</style>
